import { WhatsAppMessage, MessageType, MediaType, Platform, ParsingStats } from '@/types/whatsapp';
import { parse, isValid } from 'date-fns';
import { enUS, enGB } from 'date-fns/locale';

/** Extended date formats supporting US, UK, ISO, and Android bracket formats */
const DATE_FORMATS = [
  // US formats (MM/DD)
  'M/d/yy, H:mm',
  'M/d/yy, h:mm a',
  'M/d/yyyy, H:mm',
  'M/d/yyyy, h:mm a',
  'M/d/yyyy, H:mm:ss',
  'M/d/yyyy, h:mm:ss a',

  // UK/EU formats (DD/MM)
  'd/M/yy, H:mm',
  'd/M/yy, h:mm a',
  'd/M/yyyy, H:mm',
  'd/M/yyyy, h:mm a',
  'd/M/yyyy, H:mm:ss',
  'd/M/yyyy, h:mm:ss a',

  // ISO-style with dashes
  'yyyy-M-d, H:mm',
  'yyyy-M-d, H:mm:ss',
  'd-M-yy, H:mm',
  'd-M-yyyy, H:mm',

  // European with dots
  'd.M.yy, H:mm',
  'd.M.yyyy, H:mm',
  'd.M.yy, H:mm:ss',
  'd.M.yyyy, H:mm:ss',

  // With seconds
  'd/M/yy, H:mm:ss',
  'M/d/yy, H:mm:ss',
];

/** System message patterns to detect non-user messages */
const SYSTEM_MESSAGE_PATTERNS = [
  /Messages and calls are end-to-end encrypted/i,
  /created group/i,
  /added you/i,
  /left$/i,
  /was removed$/i,
  /changed the subject/i,
  /changed this group's icon/i,
  /security code changed/i,
  /changed the group description/i,
  /You're now an admin/i,
  /is no longer an admin/i,
  /Waiting for this message/i,
  /joined using this group's invite link/i,
];

/** Media patterns to detect different media types */
const MEDIA_PATTERNS: Record<MediaType, RegExp> = {
  image: /<Media omitted>|image omitted|photo omitted|\.(jpg|jpeg|png|gif|webp)$/i,
  video: /video omitted|\.(mp4|mov|avi|mkv)$/i,
  audio: /audio omitted|voice message omitted|\.(mp3|ogg|opus|m4a)$/i,
  document: /document omitted|file omitted|\.(pdf|doc|docx|xls|xlsx)$/i,
  sticker: /sticker omitted|GIF omitted/i,
};

/** Deleted message patterns */
const DELETED_PATTERNS = [
  /This message was deleted/i,
  /You deleted this message/i,
  /This message has been deleted/i,
];

/** Location pattern */
const LOCATION_PATTERN = /location:|https:\/\/maps\.google\.com/i;

/** Contact pattern */
const CONTACT_PATTERN = /contact card omitted|\.vcf$/i;

/**
 * Detects the date format preference based on sample dates.
 * Returns 'UK' if day > 12, 'US' if month > 12, 'unknown' otherwise.
 */
export function detectDateFormat(sampleDates: string[]): 'US' | 'UK' | 'unknown' {
  for (const date of sampleDates) {
    const parts = date.match(/(\d{1,2})[/.-](\d{1,2})/);
    if (parts) {
      const first = parseInt(parts[1], 10);
      const second = parseInt(parts[2], 10);
      if (first > 12) return 'UK'; // DD/MM - day can't be > 12 in month position
      if (second > 12) return 'US'; // MM/DD - day is in second position
    }
  }
  return 'unknown';
}

/**
 * Parses a WhatsApp date string trying multiple formats.
 */
export function parseWhatsAppDate(dateStr: string): Date {
  // Remove brackets if present (iOS format)
  const cleanDateStr = dateStr.replace(/^\[|\]$/g, '').trim();

  const locales = [enGB, enUS];

  for (const locale of locales) {
    for (const format of DATE_FORMATS) {
      const parsed = parse(cleanDateStr, format, new Date(), { locale });
      if (isValid(parsed)) {
        return parsed;
      }
    }
  }

  throw new Error(`Invalid date format: ${dateStr}`);
}

/**
 * Detects the platform that exported the chat based on format patterns.
 */
export function detectPlatform(lines: string[]): Platform {
  const sample = lines.slice(0, 50).join('\n');

  // iOS uses brackets around timestamp
  if (/^\[/.test(sample)) return 'ios';

  // Android uses "date - sender: message" format
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4},?\s*\d{1,2}:\d{2}\s*-\s*[^:]+:/.test(sample)) return 'android';

  // Web export detection (less common)
  if (/^\d{1,2}\/\d{1,2}\/\d{2,4},?\s*\d{1,2}:\d{2}(?::\d{2})?\s*[AaPp][Mm]\s*-/.test(sample)) return 'web';

  return 'unknown';
}

/**
 * Checks if a message content is a system message.
 */
export function isSystemMessage(content: string): boolean {
  return SYSTEM_MESSAGE_PATTERNS.some(p => p.test(content));
}

/**
 * Detects if a message is a deleted message.
 */
export function isDeletedMessage(content: string): boolean {
  return DELETED_PATTERNS.some(p => p.test(content));
}

/**
 * Detects the media type from message content.
 */
export function detectMediaType(content: string): MediaType | undefined {
  for (const [type, pattern] of Object.entries(MEDIA_PATTERNS)) {
    if (pattern.test(content)) {
      return type as MediaType;
    }
  }
  return undefined;
}

/**
 * Determines the message type based on content.
 */
export function determineMessageType(content: string, isSysMsg: boolean): { type: MessageType; mediaType?: MediaType } {
  if (isSysMsg) {
    return { type: 'system' };
  }

  if (isDeletedMessage(content)) {
    return { type: 'deleted' };
  }

  const mediaType = detectMediaType(content);
  if (mediaType) {
    return { type: 'media', mediaType };
  }

  if (LOCATION_PATTERN.test(content)) {
    return { type: 'location' };
  }

  if (CONTACT_PATTERN.test(content)) {
    return { type: 'contact' };
  }

  return { type: 'text' };
}

/**
 * Tries to parse a single line as a WhatsApp message.
 * Returns the parsed message or null if line doesn't match message format.
 */
function tryParseLine(line: string, lineNumber: number): WhatsAppMessage | null {
  // iOS format: [DD/MM/YYYY, HH:MM:SS] Sender: Message
  const iosRegex = /^\[(\d{1,2}\/\d{1,2}\/(?:\d{2}|\d{4}),\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?)\]\s*([^:]+):\s*(.+)$/;

  // Android format: DD/MM/YYYY, HH:MM - Sender: Message
  const androidRegex = /^(\d{1,2}\/\d{1,2}\/(?:\d{2}|\d{4}),\s*\d{1,2}:\d{2}(?::\d{2})?(?:\s*[AaPp][Mm])?)\s*[-:]\s*([^:]+):\s*(.+)$/;

  // Try iOS format first
  let match = line.match(iosRegex);
  if (!match) {
    // Try Android format
    match = line.match(androidRegex);
  }

  if (!match) {
    return null;
  }

  const [, timestamp, sender, content] = match;

  let messageDate: Date;
  try {
    messageDate = parseWhatsAppDate(timestamp);
  } catch {
    return null;
  }

  const trimmedContent = content.trim();
  const isSysMsg = isSystemMessage(trimmedContent);
  const { type, mediaType } = determineMessageType(trimmedContent, isSysMsg);

  return {
    timestamp: messageDate,
    sender: sender.trim(),
    content: trimmedContent,
    isSystemMessage: isSysMsg,
    messageType: type,
    mediaType,
    lineNumber,
  };
}

/**
 * Parses WhatsApp chat with support for multi-line messages.
 * Continuation lines (lines that don't match message format) are appended to the previous message.
 */
function parseWithContinuation(lines: string[]): { messages: WhatsAppMessage[]; stats: ParsingStats } {
  const messages: WhatsAppMessage[] = [];
  let currentMessage: WhatsAppMessage | null = null;
  const platform = detectPlatform(lines);

  let skippedLines = 0;
  let systemMessages = 0;
  let mediaMessages = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNumber = i + 1;

    // Skip empty lines at the start or between messages
    if (!line.trim()) {
      if (!currentMessage) {
        skippedLines++;
      }
      continue;
    }

    const parsed = tryParseLine(line, lineNumber);

    if (parsed) {
      // Save the previous message if exists
      if (currentMessage) {
        messages.push(currentMessage);
        if (currentMessage.isSystemMessage) systemMessages++;
        if (currentMessage.messageType === 'media') mediaMessages++;
      }
      currentMessage = parsed;
    } else if (currentMessage && line.trim()) {
      // Continuation line - append to current message
      currentMessage.content += '\n' + line;
    } else {
      skippedLines++;
    }
  }

  // Don't forget the last message
  if (currentMessage) {
    messages.push(currentMessage);
    if (currentMessage.isSystemMessage) systemMessages++;
    if (currentMessage.messageType === 'media') mediaMessages++;
  }

  const stats: ParsingStats = {
    totalLines: lines.length,
    parsedMessages: messages.length,
    skippedLines,
    systemMessages,
    mediaMessages,
    platform,
  };

  return { messages, stats };
}

/**
 * Result of parsing a WhatsApp chat file.
 */
export interface ParseResult {
  messages: WhatsAppMessage[];
  stats: ParsingStats;
}

/**
 * Parses a WhatsApp chat export file.
 */
export async function parseWhatsAppChat(file: File): Promise<WhatsAppMessage[]> {
  try {
    const text = await file.text();
    const lines = text.split('\n');

    const { messages, stats } = parseWithContinuation(lines);

    if (messages.length === 0) {
      throw new Error('No valid messages found in the chat file');
    }

    // Log parsing stats for debugging
    console.log('Parsing stats:', stats);

    return messages;
  } catch (error) {
    console.error('Error parsing WhatsApp chat:', error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to parse WhatsApp chat file. Please ensure it's a valid export."
    );
  }
}

/**
 * Parses a WhatsApp chat export file and returns both messages and parsing stats.
 */
export async function parseWhatsAppChatWithStats(file: File): Promise<ParseResult> {
  try {
    const text = await file.text();
    const lines = text.split('\n');

    const result = parseWithContinuation(lines);

    if (result.messages.length === 0) {
      throw new Error('No valid messages found in the chat file');
    }

    return result;
  } catch (error) {
    console.error('Error parsing WhatsApp chat:', error);
    throw new Error(
      error instanceof Error
        ? error.message
        : "Failed to parse WhatsApp chat file. Please ensure it's a valid export."
    );
  }
}

/**
 * Validates a WhatsApp export file.
 */
export function validateWhatsAppFile(file: File): boolean {
  // Check file type
  if (file.type !== 'text/plain') {
    return false;
  }

  // Check file size (max 10MB)
  const MAX_FILE_SIZE = 10 * 1024 * 1024;
  if (file.size > MAX_FILE_SIZE) {
    return false;
  }

  return true;
}
